import { motion } from "framer-motion";
import { Layers, GitBranch, FileText, Route } from "lucide-react";

const SOLUTIONS = [
    {
        n: "01",
        icon: Layers,
        title: "Carbon Management",
        body:
            "Establish the boundary, baseline, and operating cadence of an entity's carbon inventory. GHG Protocol aligned, ISO 14064-1 ready, and structured to feed straight into credit models, procurement scorecards, and board reporting.",
        chips: ["Boundary Setting", "Activity Data", "Ongoing Governance", "Audit Trail"]
    },
    {
        n: "02",
        icon: GitBranch,
        title: "Supplier Emissions — Scope 1, 2 & 3",
        body:
            "Map upstream and downstream vendor emissions with priority Scope 3 focus. Structured supplier engagement, CBAM-grade documentation for exporters, and portfolio-level Scope 3 attribution for banks and buyers.",
        chips: ["Scope 1 & 2 Inventory", "Priority Scope 3", "Supplier Workflows", "CBAM Dossier"]
    },
    {
        n: "03",
        icon: FileText,
        title: "Sustainability Reporting",
        body:
            "Assurance-grade disclosures from a single source of truth. BRSR Core, IFRS S2, TCFD, and voluntary frameworks — one dataset, many outputs, verification-ready by design.",
        chips: ["BRSR Core", "IFRS S2 / TCFD", "GRI Alignment", "Assurance-Ready"]
    },
    {
        n: "04",
        icon: Route,
        title: "Decarbonization Strategy",
        body:
            "A ranked, capex-aware abatement roadmap. Marginal abatement cost curves, sector-specific playbooks, renewable procurement, and vetted residual instruments — modelled to your P&L, not a template.",
        chips: ["MACC Curve", "Capex Sequencing", "Renewables & PPAs", "Residual Instruments"]
    }
];

export default function SolutionAreas() {
    return (
        <section className="relative py-24 lg:py-36 border-t border-navy/10" data-testid="solution-areas-section">
            <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-20">
                    <div className="lg:col-span-6">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-10 h-px bg-gold"></div>
                            <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-gold">Our Solutions</span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em] text-navy">
                            Four practice areas. <em className="text-gold not-italic font-light">One operating layer</em> for carbon-linked risk.
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8 lg:self-end">
                        <p className="text-navy/60 text-lg leading-relaxed">
                            From baseline inventory to abatement roadmap — Carbonis operates as a single instrument stack for MSMEs, banks, and regulators.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-navy/10 border border-navy/10">
                    {SOLUTIONS.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <motion.article
                                key={s.n}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.7, delay: i * 0.08 }}
                                className="group bg-white p-8 lg:p-12 flex flex-col gap-6 min-h-[420px] hover:bg-paper-50 transition-colors duration-500"
                                data-testid={`solution-${s.n}`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="w-12 h-12 border border-navy/15 flex items-center justify-center group-hover:border-gold transition-colors duration-500">
                                        <Icon size={20} strokeWidth={1.4} className="text-gold" />
                                    </div>
                                    <span className="font-serif text-4xl text-gold leading-none">{s.n}</span>
                                </div>
                                <div>
                                    <h3 className="font-serif text-3xl md:text-4xl leading-tight tracking-[-0.02em] text-navy mb-4">
                                        {s.title}
                                    </h3>
                                    <p className="text-navy/60 text-[15px] leading-relaxed max-w-[520px]">{s.body}</p>
                                </div>
                                <div className="mt-auto pt-6 border-t border-navy/10 flex flex-wrap gap-2">
                                    {s.chips.map((c) => (
                                        <span
                                            key={c}
                                            className="text-[10px] font-mono uppercase tracking-[0.22em] text-navy/70 border border-navy/15 px-3 py-2"
                                        >
                                            {c}
                                        </span>
                                    ))}
                                </div>
                            </motion.article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
